/** Normalize VITE_BASE_PATH / Vite `base` (always trailing slash, except root). */
export function normalizeBasePath(value: string | undefined): string {
  if (!value || value === "/") {
    return "/";
  }
  return value.endsWith("/") ? value : `${value}/`;
}

/** Resolve Vite `base` for dev vs production deploy (S3 subpath /api-portal/). */
export function resolveAppBasePath(
  mode: string,
  configuredPath: string | undefined,
): string {
  if (configuredPath) {
    return normalizeBasePath(configuredPath);
  }
  if (mode === "production") {
    return "/api-portal/";
  }
  return "/";
}

/** React Router basename (no trailing slash; undefined at site root). BrowserRouter only — do not pass to HashRouter. */
export function getRouterBasename(): string | undefined {
  const base = import.meta.env.BASE_URL;
  if (base === "/") {
    return undefined;
  }
  return base.replace(/\/$/, "");
}

/**
 * Align window pathname + hash before first paint for subpath deploys.
 * Vite assets resolve under `/api-portal/`; legacy path URLs must become hash routes.
 * HashRouter matches routes from the hash only — basename must stay unset.
 */
export function normalizeHashRouterUrl(): void {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  if (!base) {
    const { pathname, search, hash } = window.location;
    if (hash.startsWith("#/") && hash.length > 2) {
      return;
    }
    if (pathname !== "/" && pathname !== "") {
      const route = pathname.replace(/\/index\.html$/, "") + search;
      window.history.replaceState(null, "", `/#${route}`);
    }
    return;
  }

  const { pathname, search, hash } = window.location;
  const defaultHashRoute = "/";

  if (pathname === base || pathname === `${base}/`) {
    if (!hash || hash === "#") {
      // Keep query inside the hash so HashRouter / useSearchParams can read it.
      window.history.replaceState(null, "", `${base}/#/${search}`);
    }
    return;
  }

  if (pathname.startsWith(`${base}/`)) {
    let route = pathname.slice(base.length) || defaultHashRoute;
    route = route.replace(/\/index\.html$/, "").replace(/\/$/, "") || defaultHashRoute;
    const hashRoute =
      hash.startsWith("#/") && hash.length > 2 ? hash.slice(1) : route;
    // Path-style URLs (no #) must land as `#/route?query` — email clients and
    // S3 fallbacks often strip the hash, and HashRouter only reads search in-hash.
    window.history.replaceState(null, "", `${base}/#${hashRoute}${search}`);
    return;
  }

  const hashRoute =
    hash.startsWith("#/") && hash.length > 2 ? hash.slice(1) : defaultHashRoute;
  window.history.replaceState(null, "", `${base}/#${hashRoute}${search}`);
}

/** Prefix absolute app paths for static assets and plain `<a href>`. */
export function withBasePath(path: string): string {
  const base = import.meta.env.BASE_URL;
  if (base === "/") {
    return path.startsWith("/") ? path : `/${path}`;
  }
  const prefix = base.replace(/\/$/, "");
  if (path.startsWith(`${prefix}/`) || path === prefix) {
    return path;
  }
  return path.startsWith("/") ? `${prefix}${path}` : `${base}${path}`;
}

/** Prefix root-absolute `src` / `href` in build-time portal doc HTML before first paint. */
export function rewriteStaticPathsInHtml(html: string): string {
  const prefixPath = (path: string) =>
    path.startsWith("/") ? withBasePath(path) : path;

  return html
    .replace(/\ssrc="(\/[^"]*)"/gi, (_match, path: string) => ` src="${prefixPath(path)}"`)
    .replace(/\shref="(\/[^"]*)"/gi, (_match, path: string) => ` href="${prefixPath(path)}"`);
}

/** Static Redoc/API assets under `/glofox/api/` — not SPA hash routes. */
function isGlofoxStaticAssetHref(route: string): boolean {
  const path = route.split("#")[0];
  return path.startsWith("/glofox/api/");
}

/**
 * Rewrite in-app doc links to hash-router URLs for copy-link / open-in-new-tab.
 * Keep href builders aligned with scripts/lib/app-base-path.mjs.
 */
export function rewriteSpaLinksToHashInHtml(html: string): string {
  const base = import.meta.env.BASE_URL.replace(/\/$/, "");
  const prefix = base === "" ? "" : base;

  return html.replace(
    /\shref="([^"]*)"/gi,
    (match, href: string) => {
      if (
        !href.startsWith("/glofox") &&
        !href.startsWith("/login") &&
        !href.startsWith("/signup") &&
        !(base !== "/" && (href.startsWith(`${base}/glofox`) || href.startsWith(`${base}/login`)))
      ) {
        return match;
      }

      let route = href;
      if (base !== "/" && href.startsWith(base)) {
        route = href.slice(base.length) || "/";
      }

      if (isGlofoxStaticAssetHref(route)) {
        return match;
      }

      return ` href="${prefix}/#${route}"`;
    },
  );
}
